import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { db } from "@/config/firebaseConfig";
import { useBuyerVehicleLists } from "./useBuyerVehicleLists";
import {
  matchesDashboardVehicleSearch,
  normalizeDashboardSearchText,
} from "@/app/helpers/dashboardVehicleSearch";

/* IDs in ≤30er Blöcke (Limit für 'in') */
const chunkArray = (arr, size = 30) =>
  arr.reduce((chunks, item, i) => {
    const idx = Math.floor(i / size);
    (chunks[idx] ||= []).push(item);
    return chunks;
  }, []);

export const useFetchWonVehicles = (
  userData,
  { page = 1, pageSize = 50, searchQuery = "" } = {}
) => {
  userData = useBuyerVehicleLists(userData);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const ids = Array.isArray(userData?.wonVehicles)
      ? userData.wonVehicles
      : [];
    const trimmedQuery = normalizeDashboardSearchText(searchQuery);

    if (!ids.length) {
      setVehicles([]);
      setTotalItems(0);
      setLoading(false);
      return;
    }

    const fetchByIds = async (requestedIds) => {
      if (!requestedIds.length) return [];

      const vehiclesRef = collection(db, "USERTOFIN");
      const chunks = chunkArray(requestedIds, 30);

      const snaps = await Promise.all(
        chunks.map((chunkIds) =>
          getDocs(query(vehiclesRef, where("id", "in", chunkIds)))
        )
      );

      const byId = new Map();
      snaps.forEach((snap) => {
        snap.docs.forEach((d) => {
          const data = d.data();
          const key = data?.id ?? d.id;
          byId.set(key, { id: d.id, ...data });
        });
      });

      return requestedIds.map((id) => byId.get(id)).filter(Boolean);
    };

    if (trimmedQuery) {
      (async () => {
        try {
          setLoading(true);
          const allVehicles = await fetchByIds(ids);
          const filteredVehicles = allVehicles.filter((vehicle) =>
            matchesDashboardVehicleSearch(vehicle, trimmedQuery)
          );
          const start = (page - 1) * pageSize;
          const pageSlice = filteredVehicles.slice(start, start + pageSize);

          if (cancelled) return;
          setVehicles(pageSlice);
          setTotalItems(filteredVehicles.length);
        } catch (err) {
          console.error("Firestore error (wonVehicles search):", err);
          if (!cancelled) {
            setVehicles([]);
            setTotalItems(0);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    setTotalItems(ids.length);
    setLoading(true);

    // IDs der aktuellen Seite
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const pageIds = ids.slice(start, end);

    if (!pageIds.length) {
      setVehicles([]);
      setLoading(false);
      return;
    }

    const vehiclesRef = collection(db, "USERTOFIN");
    const chunks = chunkArray(pageIds, 30);
    const unsubscribes = [];
    const byId = new Map();

    chunks.forEach((chunkIds) => {
      const q = query(vehiclesRef, where("id", "in", chunkIds));
      const unsub = onSnapshot(
        q,
        (snap) => {
          if (cancelled) return;
          snap.docs.forEach((d) => {
            const data = d.data();
            const key = data?.id ?? d.id;
            byId.set(key, { id: d.id, ...data });
          });
          // Reihenfolge wie pageIds
          const ordered = pageIds.map((id) => byId.get(id)).filter(Boolean);
          setVehicles(ordered);
          setLoading(false);
        },
        (err) => {
          if (cancelled) return;
          console.error("Firestore error (wonVehicles paginated):", err);
          setLoading(false);
        }
      );
      unsubscribes.push(unsub);
    });

    return () => {
      cancelled = true;
      unsubscribes.forEach((u) => u());
    };
  }, [userData?.wonVehicles, page, pageSize, searchQuery]);

  return {
    loading: loading || userData.buyerListsLoading,
    vehicles,
    wonVehicles: vehicles,
    totalItems,
  };
};
